import { Check, Info, Minus, Users } from 'lucide-react'
import { memo, useId } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { BandPill } from '../../controls/BandPill'
import { Pill } from '../../controls/Pill'
import { formatSimilarity } from '../../format'
import { BAND_TONE, MICRO_LABEL, MONO, TEXT_CAPTION } from '../../recipes'
import type { VoiceBand, VoiceCondition } from '../voiceprint-types'
import { PAIR_CARD, PAIR_MAIN_COLUMN } from './voice-pair-recipes'
import type { VoicePair } from './voice-pair-types'

const COPY = 'humint.voice.match'
type PairEvidence = Pick<
  VoicePair,
  'status' | 'band' | 'similarity' | 'provisional' | 'conditions' | 'seenTogether'
>

/**
 * The band in words. A weak match wears the muted tone of every weak band of the workspace and
 * is named in full (*Weak match*): the workspace's own bare word names no match.
 */
function VoiceBandPill({ band }: { band: VoiceBand }) {
  const { t } = useTranslation()
  if (band !== 'weak') return <BandPill band={band} />
  return (
    <Pill tone={BAND_TONE.weak} data-band="weak">
      {t(`${COPY}.bands.weak`)}
    </Pill>
  )
}

/**
 * How strong the server found the pair: the band in words while nobody has decided, and the raw
 * figure with two decimals beside the word *Similarity* and *provisional*. A readout and nothing
 * more: no bar, ring, percent or colour that would read as "this much the same person".
 */
function Strength({ pair }: { pair: PairEvidence }) {
  const { t, i18n } = useTranslation()
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      {pair.status === 'suggested' && <VoiceBandPill band={pair.band} />}
      <p className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-sm text-muted-foreground">{t('humint.voice.lead.similarity')}</span>
        <span data-similarity className={cn(MONO, 'text-2xl leading-none font-semibold')}>
          {formatSimilarity(pair.similarity, i18n.language)}
        </span>
        {pair.provisional && (
          <span className="text-sm text-muted-foreground">
            · {t('humint.common.bands.provisional')}
          </span>
        )}
      </p>
    </div>
  )
}

/** One thing the server looked at: met or not, as an icon and a spoken word, and its figure. */
function ConditionRow({ condition }: { condition: VoiceCondition }) {
  const { t, i18n } = useTranslation()
  const Icon = condition.met ? Check : Minus
  return (
    <li
      data-condition={condition.id}
      className="flex items-start gap-2 border-border border-t py-2 text-sm first:border-t-0"
    >
      <Icon aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1">
        <span className="sr-only">
          {t(`humint.check.candidate.${condition.met ? 'conditionMet' : 'conditionMissed'}`)}{' '}
        </span>
        {t(`${COPY}.evidence.condition.${condition.id}`)}
      </span>
      {condition.value === null ? (
        <span className="text-muted-foreground">
          <span aria-hidden>—</span>
          <span className="sr-only">{t(`${COPY}.evidence.noValue`)}</span>
        </span>
      ) : (
        <span className={MONO}>{formatSimilarity(condition.value, i18n.language)}</span>
      )}
    </li>
  )
}

/**
 * What the comparison rests on (the server's judgement, shown and never re-derived): how strong
 * it found the pair, what it looked at — a neutral list, never counted up to a verdict — and the
 * two sentences every voice match carries: it is a lead, not an identification, and recordings
 * from one phone or room come out more alike than the voices are.
 */
export const VoicePairEvidence = memo(function VoicePairEvidence({ pair }: { pair: PairEvidence }) {
  const { t } = useTranslation()
  const headingId = useId()
  return (
    <section
      aria-labelledby={headingId}
      className={cn(PAIR_CARD, PAIR_MAIN_COLUMN, 'flex flex-col gap-4 p-4')}
    >
      <h3 id={headingId} className={MICRO_LABEL}>
        {t('humint.tattooMatch.evidence.heading')}
      </h3>
      <Strength pair={pair} />
      {pair.conditions.length > 0 && (
        <div className="flex flex-col gap-1">
          <h4 className={cn(TEXT_CAPTION, 'font-medium text-muted-foreground')}>
            {t(`${COPY}.evidence.conditions`)}
          </h4>
          <ul className="flex flex-col">
            {pair.conditions.map(condition => (
              <ConditionRow key={condition.id} condition={condition} />
            ))}
          </ul>
        </div>
      )}
      {pair.seenTogether && (
        <p data-seen-together className="flex items-start gap-2 text-sm font-medium">
          <Users aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          {t(`${COPY}.seenTogether`)}
        </p>
      )}
      <div className="flex flex-col gap-2 border-border border-t pt-3">
        <p className="text-sm font-medium">{t('humint.voice.lead.sentence')}</p>
        <p className={cn(TEXT_CAPTION, 'flex items-start gap-2 text-muted-foreground')}>
          <Info aria-hidden className="mt-0.5 size-3.5 shrink-0" />
          {t(`${COPY}.caution`)}
        </p>
      </div>
    </section>
  )
})
